import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Avatar,
  AvatarProps,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CardHeaderProps,
  CardProps,
  Collapse,
  IconButton,
  Tooltip,
  cardHeaderClasses,
  styled,
} from "@mui/material";

import { Patient } from "../models/patient";
import { PatientAvatar } from "./PatientAvatar";
import { PatientExports } from "./PatientExports";
import { useAppContext } from "../context/AppContext";

const avatarProps: AvatarProps = {
  sx: { height: 40, width: 40 },
};
const cardHeaderProps: CardHeaderProps = {
  sx: { [`& .${cardHeaderClasses.action}`]: { alignSelf: "center" } },
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop: string) => !["selected"].includes(prop),
})<CardProps & { selected?: boolean }>(({ selected, theme }) => ({
  listStyle: "none",
  transition: theme.transitions.create("border-color"),
  ...(selected && {
    background: theme.palette.action.focus,
    borderColor: theme.palette.primary.main,
  }),
  [`& .${cardHeaderClasses.root}`]: {
    [theme.breakpoints.up("md")]: {
      minHeight: theme.spacing(12),
    },
  },
  [`& .${cardHeaderClasses.content}`]: {
    overflow: "hidden",
  },
}));

export const PatientCard: React.FC<
  CardProps & {
    onClick?: () => void;
    patient: Patient;
    selected?: boolean;
  }
> = ({ onClick, patient, ...rootProps }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selected } = rootProps;

  const { togglePatientMenu } = useAppContext();

  const onCardActionAreaClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (onClick) {
        e.stopPropagation();
        onClick?.();
      } else {
        if (patient)
          navigate(`/protected/patient/${encodeURIComponent(patient?.id)}`);
      }
    },
    [navigate, onClick, patient]
  );

  return (
    <StyledCard {...rootProps}>
      <CardActionArea
        onClick={onCardActionAreaClick}
        sx={{
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          borderBottom: `0.75px solid`,
          borderBottomColor: "divider",
          mb: "-0.75px",
        }}
      >
        <CardHeader
          action={
            <Tooltip title={t("patient.more-tooltip")}>
              <IconButton
                aria-label={`${t("patient.more-tooltip")}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  togglePatientMenu({
                    anchorEl: e.currentTarget,
                    displayImports: false,
                    patient,
                  });
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          avatar={<PatientAvatar patient={patient} {...avatarProps} />}
          title={patient.name}
          titleTypographyProps={{ noWrap: true, variant: "subtitle1" }}
          {...cardHeaderProps}
        />
      </CardActionArea>
      <Collapse in={selected}>
        <CardContent sx={{ "&, &:last-child": { p: 0 } }}>
          <PatientExports patient={patient} />
        </CardContent>
      </Collapse>
    </StyledCard>
  );
};

export const NewPatientCard: React.FC<CardProps> = ({ ...rootProps }) => {
  const { t } = useTranslation();

  const { togglePatientDialog } = useAppContext();

  const onCardActionAreaClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      togglePatientDialog();
    },
    [togglePatientDialog]
  );

  return (
    <StyledCard {...rootProps}>
      <CardActionArea
        onClick={onCardActionAreaClick}
        sx={{
          bgcolor: "background.default",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <CardHeader
          avatar={
            <Avatar {...avatarProps}>
              <AddIcon color="primary" fontSize="small" />
            </Avatar>
          }
          title={t("patient.create-cta")}
          titleTypographyProps={{
            color: "primary",
            noWrap: true,
            variant: "subtitle1",
          }}
          {...cardHeaderProps}
        />
      </CardActionArea>
    </StyledCard>
  );
};
